import { useEffect } from 'react';
import { motion } from 'framer-motion';

export default function PaymentRequired() {
  useEffect(() => {
    // Hide ALL chat widgets while payment page is active
    const style = document.createElement('style');
    style.id = 'hide-readdy-chat-widget';
    style.textContent = `
      readdy-widget,
      vapi-widget,
      [data-readdy-widget],
      .readdy-widget-container,
      .readdy-assistant-widget,
      #readdy-widget-root,
      iframe[src*="readdy.ai/api/public/assistant"],
      iframe[src*="vapi.ai"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    // Also try to directly hide any fixed widget buttons injected by the script
    const hideInjectedWidget = () => {
      const widgetEls = document.querySelectorAll(
        '[class*="readdy"], [id*="readdy"], readdy-widget, vapi-widget, [class*="vapi"], [id*="vapi"]'
      );
      widgetEls.forEach((el) => {
        (el as HTMLElement).style.display = 'none';
        (el as HTMLElement).style.visibility = 'hidden';
      });
    };
    hideInjectedWidget();
    const interval = setInterval(hideInjectedWidget, 500);

    return () => {
      const existing = document.getElementById('hide-readdy-chat-widget');
      if (existing) existing.remove();
      clearInterval(interval);
    };
  }, []);

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden"
      style={{ fontFamily: "'Montserrat', sans-serif", background: '#0a0a0a' }}
    >
      {/* Google Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;900&display=swap"
        rel="stylesheet"
      />

      {/* Background texture / gradient layers */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 20% 50%, rgba(220,38,38,0.18) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(220,38,38,0.10) 0%, transparent 50%)',
          }}
        />
        {/* Grid lines */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Big decorative "OFFLINE" text behind */}
      <div
        className="absolute select-none pointer-events-none z-0 font-black text-center leading-none"
        style={{
          fontSize: 'clamp(80px, 20vw, 220px)',
          color: 'rgba(220,38,38,0.04)',
          letterSpacing: '-0.05em',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          whiteSpace: 'nowrap',
        }}
      >
        OFFLINE
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 py-12 flex flex-col items-center text-center">

        {/* Top: Melt Graphics logo */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="mb-12"
        >
          <img
            src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/c095d581dc1845a3b1c3b759951a8d5b.png"
            alt="Melt Graphics"
            className="h-20 w-auto object-contain mx-auto"
            style={{ filter: 'brightness(1.1)' }}
          />
        </motion.div>

        {/* Sad face + faded client brand */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex flex-col items-center mb-10"
        >
          <div className="text-8xl mb-5 select-none" role="img" aria-label="sad face">😢</div>
          {/* Faded ExpoHood brand */}
          <div className="opacity-20 grayscale select-none flex flex-col items-center gap-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-fire-line text-white text-xl"></i>
              </div>
              <span className="text-3xl font-black text-white tracking-tight">
                ExpoHood <span className="text-orange-400">Cleaning</span>
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-1">Professional Kitchen Hood Cleaning Services</p>
          </div>
        </motion.div>

        {/* Big red "SITE DOWN" badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-8"
        >
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold tracking-widest uppercase"
            style={{ background: 'rgba(220,38,38,0.15)', color: '#f87171', border: '1px solid rgba(220,38,38,0.3)' }}
          >
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block"></span>
            Site Unavailable
          </span>
        </motion.div>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="font-black text-white leading-tight mb-6"
          style={{ fontSize: 'clamp(40px, 8vw, 88px)', letterSpacing: '-0.03em' }}
        >
          This Site Is{' '}
          <span style={{ color: '#ef4444' }}>Down</span>
          <br />
          Due to Payment
        </motion.h1>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="text-gray-400 mb-14 max-w-xl mx-auto"
          style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', lineHeight: 1.7 }}
        >
          This website is temporarily offline due to a pending payment.
          To get this site back up and running, please contact us directly.
        </motion.p>

        {/* Contact cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14"
        >
          {/* Phone */}
          <a
            href="tel:5743276106"
            className="group flex flex-col items-center gap-4 rounded-2xl p-8 transition-all duration-300 cursor-pointer"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(220,38,38,0.12)';
              (e.currentTarget as HTMLElement).style.border = '1px solid rgba(220,38,38,0.4)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
              (e.currentTarget as HTMLElement).style.border = '1px solid rgba(255,255,255,0.08)';
            }}
          >
            <div
              className="w-16 h-16 flex items-center justify-center rounded-2xl"
              style={{ background: 'rgba(220,38,38,0.15)' }}
            >
              <i className="ri-phone-fill text-red-400 text-3xl"></i>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-semibold uppercase tracking-widest mb-1">Call Us</p>
              <p className="text-white font-black text-2xl tracking-tight">574-327-6106</p>
            </div>
          </a>

          {/* Email */}
          <a
            href="mailto:meltgraphicsusa@gmail.com"
            className="group flex flex-col items-center gap-4 rounded-2xl p-8 transition-all duration-300 cursor-pointer"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(220,38,38,0.12)';
              (e.currentTarget as HTMLElement).style.border = '1px solid rgba(220,38,38,0.4)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
              (e.currentTarget as HTMLElement).style.border = '1px solid rgba(255,255,255,0.08)';
            }}
          >
            <div
              className="w-16 h-16 flex items-center justify-center rounded-2xl"
              style={{ background: 'rgba(220,38,38,0.15)' }}
            >
              <i className="ri-mail-fill text-red-400 text-3xl"></i>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-semibold uppercase tracking-widest mb-1">Email Us</p>
              <p className="text-white font-bold text-lg whitespace-nowrap">meltgraphicsusa@gmail.com</p>
            </div>
          </a>
        </motion.div>

        {/* CTA button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="flex flex-col sm:flex-row gap-4 w-full max-w-sm mx-auto"
        >
          <a
            href="tel:5743276106"
            className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl font-bold text-lg text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ background: '#dc2626' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#b91c1c'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#dc2626'; }}
          >
            <i className="ri-phone-fill text-xl"></i>
            Reactivate This Site
          </a>
        </motion.div>

        {/* Footer credit */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="mt-16 text-gray-600 text-sm"
        >
          Designed &amp; developed by{' '}
          <span className="text-gray-400 font-semibold">Melt Graphics</span>
        </motion.p>
      </div>
    </div>
  );
}
